import { Produto } from "@/domain/entities/Produto";
import { IProdutoRepository } from "@/domain/repositories/IProdutoRepository";
import { apiClient } from "@/lib/api/apiClient";

// Backend estoque returns { success, data, meta } envelope instead of raw array
function unwrapData<T>(response: T | { data?: T }): T {
  if (response && typeof response === "object" && "data" in response) {
    const r = response as { data?: T };
    if (r.data !== undefined) {
      return r.data;
    }
  }
  return response as T;
}

/**
 * Implementação do repositório de Produto usando apiClient
 */
export class ApiProdutoRepository implements IProdutoRepository {
  async findById(id: string): Promise<Produto | null> {
    try {
      const data = await apiClient.get<Produto>(`/estoque/produtos/${id}`);
      // As the API might return the domain object directly or raw data
      // we typecast it or map it if necessary via a Mapper if backend structure differs,
      // but typically apiClient unifies this. We assume the API returns the mapped Domain type.
      return data;
    } catch (error) {
      // @ts-expect-error — TS2571
      if ((error as unknown).status === 404) return null;
      throw error;
    }
  }

  async findByCodigoBarras(
    codigoBarras: string,
    clinicId: string,
  ): Promise<Produto | null> {
    try {
      const data = await apiClient.get<Produto>(
        `/estoque/produtos/codigo/${codigoBarras}`,
        {
          params: { clinicId },
        },
      );
      return data;
    } catch (error) {
      // @ts-expect-error — TS2571
      if ((error as unknown).status === 404) return null;
      throw error;
    }
  }

  async findByClinicId(clinicId: string): Promise<Produto[]> {
    const response = await apiClient.get<Produto[] | { data?: Produto[] }>("/estoque/produtos", {
      params: { clinicId },
    });
    return unwrapData(response) || [];
  }

  async findActiveByClinicId(clinicId: string): Promise<Produto[]> {
    const response = await apiClient.get<Produto[] | { data?: Produto[] }>("/estoque/produtos", {
      params: { clinicId, ativo: true },
    });
    return unwrapData(response) || [];
  }

  async findByCategoria(
    clinicId: string,
    categoria:
      | "MATERIAL"
      | "MEDICAMENTO"
      | "EQUIPAMENTO"
      | "CONSUMIVEL"
      | "OUTRO",
  ): Promise<Produto[]> {
    const response = await apiClient.get<Produto[] | { data?: Produto[] }>("/estoque/produtos", {
      params: { clinicId, categoria },
    });
    return unwrapData(response) || [];
  }

  async findEstoqueBaixo(clinicId: string): Promise<Produto[]> {
    const response = await apiClient.get<Produto[] | { data?: Produto[] }>("/estoque/produtos", {
      params: { clinicId, filter: "baixo_estoque" },
    });
    return unwrapData(response) || [];
  }

  async findEstoqueZerado(clinicId: string): Promise<Produto[]> {
    const response = await apiClient.get<Produto[] | { data?: Produto[] }>("/estoque/produtos", {
      params: { clinicId, filter: "zerado" },
    });
    return unwrapData(response) || [];
  }

  async save(produto: Produto): Promise<void> {
    await apiClient.post("/estoque/produtos", produto);
  }

  async update(produto: Produto): Promise<void> {
    await apiClient.patch(`/estoque/produtos/${produto.id}`, produto);
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/estoque/produtos/${id}`);
  }
}
